import { GA_CATEGORIES, GA_EVENTS, ITEM_SOURCES } from "../../../../data/gaConstants";
import { sendGTMEvent } from "../../../../effects-bridge";
import { isNil } from "lodash";
import { SaleItem } from "../../../../models/saleItem";
import { Action } from "redux-saga";
import { getLocalStorage } from "../../../../utils/localStorage";
import { LOCALSTORAGE_KEYS } from "../../../../utils/constants";
import { handleCartCreatedOrEmptied, handleCartSessionUpdate, handleCartDeleted } from "./cartEventHandler";
// The suggestive-selling flow's own home. `helpers/recommenderHelpers` is only a shim over it now.
import {
  getSuggestiveSellingProvider,
  isSuggestiveSellingEnabled,
} from "../../../../recommendations";

/**
 * WEB-ONLY module — `redux-store/middleware/saleUpdate/cartEventEffect`. One variant, for the
 * reason given in `./cartEventHandler/index.desktop.ts`.
 *
 * ⚠️ Two substitutions against the app original, both standing on this branch:
 *   - `sendGTMEvent` comes from the effects bridge, not from an app analytics helper;
 *   - `getLocalStorage` comes from `utils/localStorage`, not from the app's `utils` barrel.
 * Everything else is transcribed unchanged, including the `getItemKey` collision rule and the
 * two-pass removal detection.
 */

function getItemKey(item: SaleItem) {
  if (!isNil(item.itemNumber)) {
    return item.itemId + item.itemNumber;
  } else {
    return item.itemId;
  }
}

/**
 * Detects changes between previous and current cart states, and triggers:
 *
 * 1. Cart audit events (via cartEventHandler):
 *    - CART_CREATED      — first item added to empty cart
 *    - CART_EMPTIED      — last item removed from cart
 *
 * 2. GA item events (via sendGTMEvent):
 *    - ITEM_ADDED_TO_CART
 *    - ITEM_QTY_CHANGED
 *    - ITEM_REMOVED_FROM_CART
 *
 * If a SaleItem has `recommendationInfo`, it is tagged with `itemSource: "Recommendation"`.
 */
function triggerCartGAEvents(
  prevSaleId: string,
  curSaleId: string,
  previousCartItems: SaleItem[],
  currentCartItems: SaleItem[],
  recommendationsEnabled: boolean,
  recommendationsProvider?: string,
) {
  // Delegate CART_CREATED/CART_EMPTIED/CART_LONG_DURATION to cart event handler.
  // It returns `true` if the sale ID changed (new sale created),
  // in which case we should stop — comparing items across different sales
  // would generate incorrect ITEM_ADDED/ITEM_REMOVED GA events.
  handleCartCreatedOrEmptied(prevSaleId, curSaleId, previousCartItems, currentCartItems);

  if (prevSaleId !== curSaleId) return;

  const prevMap = new Map<string, SaleItem>();
  const currMap = new Map<string, SaleItem>();

  previousCartItems.forEach((item) => prevMap.set(getItemKey(item), item));
  currentCartItems.forEach((item) => currMap.set(getItemKey(item), item));
  const recommendedItems = JSON.parse(
    getLocalStorage(LOCALSTORAGE_KEYS.recommendedItems) || "[]"
  );

  // Check for added items or quantity changes
  for (const [key, currItem] of currMap) {
    const itemId = currItem.itemId;
    const prevItem = prevMap.get(key);

    const itemSource = currItem.recommendationInfo
      ? ITEM_SOURCES.RECOMMENDATIONS
      : currItem?.isSearchView
        ? ITEM_SOURCES.SEARCH
        : ITEM_SOURCES.CATALOG;

    // if the item was being shown on the UI as a recommendation
    let wasRecommended;
    if (recommendationsEnabled) {
      wasRecommended =
        recommendedItems.findIndex(
          (item: any) => item?.mid === currItem?.itemId
        ) !== -1;
    }

    if (!prevItem && currItem.quantity > 0) {
      // Completely new item
      sendGTMEvent({
        eventName: GA_EVENTS.ITEM_ADDED_TO_CART,
        params: {
          category: GA_CATEGORIES.SALE,
          itemId,
          saleId: curSaleId,
          itemSource,
          itemName: currItem.longName,
          itemPrice: currItem.unitPrice,
          recommendationId: currItem.recommendationInfo?.recommendationId,
          wasRecommended,
          recommendationsProvider,
        },
      });
    } else if (prevItem) {
      if (prevItem.quantity !== currItem.quantity) {
        if (currItem.quantity === 0) {
          // Qty dropped to 0 → treat as removal
          sendGTMEvent({
            eventName: GA_EVENTS.ITEM_REMOVED_FROM_CART,
            params: {
              category: GA_CATEGORIES.SALE,
              itemId,
              itemPrice: currItem.unitPrice,
              saleId: curSaleId,
              itemName: currItem.longName,
              recommendationId: currItem.recommendationInfo?.recommendationId,
            },
          });
          // prevent duplicate removal in second loop
          prevMap.delete(key);
        } else {
          // Normal qty change
          sendGTMEvent({
            eventName: GA_EVENTS.ITEM_QTY_CHANGED,
            params: {
              category: GA_CATEGORIES.SALE,
              itemId,
              itemPrice: currItem.unitPrice,
              itemName: currItem.longName,
              saleId: curSaleId,
              recommendationId: currItem.recommendationInfo?.recommendationId,
            },
          });
        }
      }
    }
  }

  // Check for items removed entirely (not present in current cart)
  for (const [key, prevItem] of prevMap) {
    if (!currMap.has(key)) {
      sendGTMEvent({
        eventName: GA_EVENTS.ITEM_REMOVED_FROM_CART,
        params: {
          category: GA_CATEGORIES.SALE,
          itemId: prevItem.itemId,
          itemPrice: prevItem.unitPrice,
          itemName: prevItem.longName,
          saleId: curSaleId,
          recommendationId: prevItem.recommendationInfo?.recommendationId,
        },
      });
    }
  }
}

/**
 * Listener effect that fires cart audit events and GA item events
 * whenever the cart state changes via saveSaleData.
 */
export const cartEventEffect = async (action: Action, listenerApi: any) => {
  const currentState = listenerApi.getState();
  const prevState = listenerApi.getOriginalState();

  const prevSale = prevState?.saleDataStore?.saleData;
  const currentSale = currentState?.saleDataStore?.saleData;

  const prevSaleItems = prevSale?.saleItems || [];
  const currentSaleItems = currentSale?.saleItems || [];

  const recommendationsEnabled = isSuggestiveSellingEnabled(currentState);
  const recommendationsProvider = getSuggestiveSellingProvider(currentState);

  //  Cart events
  triggerCartGAEvents(
    prevSale?.mid,
    currentSale?.mid,
    prevSaleItems,
    currentSaleItems,
    recommendationsEnabled,
    recommendationsProvider,
  );

  // SAFE ADDITION (only for saveSaleData)
  const saleValidated = prevState?.saleDataStore?.saleValidated;
  handleCartSessionUpdate(currentSale);
  handleCartDeleted(prevSale, currentSale, saleValidated);

};
